/**
 * Vendor Master — Phase 1 of the procurement overhaul.
 *
 * Unified vendor registry for ALL procurement: material suppliers,
 * subcontractors, consultants, equipment hire, AMC providers.
 *
 * Layout: left rail (type / empanelment / compliance filters) ·
 * card grid · right drawer (profile with tabs) · add-vendor sheet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Building2, Plus, Search, Star, ShieldCheck, ShieldAlert, FileWarning,
  Loader2, Phone, Mail, MapPin, Landmark, FileText, Clock, Ban, CheckCircle2, Upload,
} from 'lucide-react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import VendorImportWizard from '../../components/procurement/VendorImportWizard';
import { useUrlTab } from '../../lib/hooks/useUrlState';
import { useAuth } from '../../contexts/AuthContext';

const EMPANEL_DOCS = ['GST Certificate', 'PAN', 'Bank Proof'];

const VENDOR_TYPES = [
  { id: 'material_supplier', label: 'Material Supplier' },
  { id: 'subcontractor', label: 'Subcontractor' },
  { id: 'consultant', label: 'Consultant' },
  { id: 'equipment_hire', label: 'Equipment Hire' },
  { id: 'amc_provider', label: 'AMC Provider' },
  { id: 'labour_contractor', label: 'Labour Contractor' },
  { id: 'transport', label: 'Transport' },
];
const DOC_TYPES = ['pf_challan', 'esic_challan', 'labour_licence', 'wc_policy', 'gst_certificate', 'pan_card', 'msme_certificate', 'iso_certificate', 'bank_guarantee', 'other'];
const EMP_BADGE = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  blacklisted: 'bg-red-50 text-red-700 border-red-200',
  suspended: 'bg-slate-100 text-slate-600 border-slate-200',
};
const DOC_BADGE = {
  valid: 'bg-emerald-50 text-emerald-700',
  expiring_soon: 'bg-amber-50 text-amber-700',
  expired: 'bg-red-50 text-red-700',
};

const typeLabel = (id) => VENDOR_TYPES.find((t) => t.id === id)?.label || id;

function Stars({ score }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <Star size={12} className="text-amber-500 fill-amber-400" />
      <span className="font-display font-bold tabular-nums">{(score || 0).toFixed(1)}</span>
    </span>
  );
}

export default function VendorMaster() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fType, setFType] = useState('');
  const [fEmp, setFEmp] = useState('');
  const [fCompliance, setFCompliance] = useState('');
  const [fReg, setFReg] = useState('');
  const [fSelf, setFSelf] = useState(false);
  const { user } = useAuth() || {};
  const canEmpanel = !!user?.is_procurement_approver;
  const [expiringCount, setExpiringCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.q = search;
      if (fType) params.vendor_type = fType;
      if (fEmp) params.empanelment_status = fEmp;
      if (fCompliance) params.compliance = fCompliance;
      if (fReg) params.registration = fReg;
      if (fSelf) params.self_approved = true;
      const d = await apiGet('/vendors', params);
      setItems(d?.items || []);
      setTotal(d?.total || 0);
      setPages(d?.pages || 1);
    } catch {
      toast.error('Failed to load vendors');
    } finally { setLoading(false); }
  }, [page, search, fType, fEmp, fCompliance, fReg, fSelf]);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { setPage(1); }, [search, fType, fEmp, fCompliance]);
  useEffect(() => {
    apiGet('/vendors/expiring-docs').then((d) => setExpiringCount(d?.count || 0)).catch(() => {});
    apiGet('/vendors/empanelment-pending-count').then((d) => setPendingCount(d?.total || 0)).catch(() => {});
  }, [loading]);

  // Reset selection when the filter/page changes
  useEffect(() => { setSelectedIds([]); }, [fEmp, page, search]);

  const bulkApprove = async () => {
    if (!selectedIds.length) return;
    setBulkBusy(true);
    try {
      const r = await apiPost('/vendors/empanel-bulk', { vendor_ids: selectedIds, action: 'approve' });
      if (r?.processed) toast.success(`${r.processed} vendor(s) approved`);
      if (r?.skipped_incomplete?.length) toast.warning(`${r.skipped_incomplete.length} skipped — registration incomplete`);
      setSelectedIds([]); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approve failed');
    } finally { setBulkBusy(false); }
  };
  const bulkReject = async () => {
    if (!selectedIds.length) return;
    const reason = window.prompt('Reason for rejection (required):');
    if (!reason || !reason.trim()) return;
    setBulkBusy(true);
    try {
      const r = await apiPost('/vendors/empanel-bulk', { vendor_ids: selectedIds, action: 'reject', reason: reason.trim() });
      if (r?.processed) toast.success(`${r.processed} vendor(s) rejected`);
      setSelectedIds([]); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk reject failed');
    } finally { setBulkBusy(false); }
  };

  const toggleSelect = (id) => setSelectedIds((cur) => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);

  const approvedCount = items.filter((v) => v.empanelment_status === 'approved').length;

  return (
    <div className="space-y-4" data-testid="vendor-master-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-display">
            <Building2 size={11} /> Procurement · Master Data
          </div>
          <h1 className="font-display font-bold text-2xl text-slate-900 mt-1 flex items-center gap-2">
            <Building2 size={22} className="text-[#3b4872]" /> Vendor Master
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Single registry for suppliers, subcontractors, consultants, hire companies &amp; AMC providers — with empanelment, compliance and rating.
          </p>
        </div>
        <button
          onClick={() => setAddOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm font-display font-bold uppercase tracking-wider"
          data-testid="vm-add-vendor-btn"
        >
          <Plus size={12} /> Add Vendor
        </button>
        <button
          onClick={() => setImportOpen(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 rounded-sm font-display font-bold uppercase tracking-wider"
          data-testid="vm-import-vendor-btn"
        >
          <Upload size={12} /> Import Excel
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={Building2} label="Total Vendors" value={total} testid="vm-kpi-total" />
        <Kpi icon={ShieldCheck} label="Approved (page)" value={approvedCount} testid="vm-kpi-approved" />
        <Kpi icon={Clock} label="Awaiting Empanelment" value={pendingCount} testid="vm-kpi-pending-empanel" />
        <Kpi icon={FileWarning} label="Docs Expiring / Expired" value={expiringCount} testid="vm-kpi-expiring" />
      </div>

      {/* Round 4 P1 — Empanelment queue: shows when filter is set to pending */}
      {fEmp === 'pending' && items.length > 0 && (
        <div className="bg-amber-50/60 border border-amber-200 rounded-sm p-3 flex items-center justify-between" data-testid="vm-empanel-queue-bar">
          <div className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={selectedIds.length === items.length && items.length > 0}
              onChange={(e) => setSelectedIds(e.target.checked ? items.map(v => v.id) : [])}
              className="w-4 h-4 accent-[#3b4872]"
              data-testid="vm-select-all"
            />
            <span className="text-slate-700"><b>{selectedIds.length}</b> selected · Empanelment queue</span>
          </div>
          {canEmpanel ? <div className="flex gap-2">
            <button
              disabled={!selectedIds.length || bulkBusy}
              onClick={bulkApprove}
              className="px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm disabled:opacity-40"
              data-testid="vm-bulk-approve"
            >{bulkBusy ? 'Working…' : `Approve ${selectedIds.length || ''}`}</button>
            <button
              disabled={!selectedIds.length || bulkBusy}
              onClick={bulkReject}
              className="px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider bg-rose-600 hover:bg-rose-700 text-white rounded-sm disabled:opacity-40"
              data-testid="vm-bulk-reject"
            >Reject</button>
          </div> : <span className="text-[11px] text-amber-800" data-testid="vm-queue-readonly">Waiting for empanelment by the Procurement team</span>}
        </div>
      )}

      <div className="flex gap-4 items-start">
        {/* Left rail filters */}
        <aside className="w-52 shrink-0 bg-white border border-slate-200 rounded-sm p-2 sticky top-4" data-testid="vm-left-rail">
          <div className="kpi-overline px-2 pt-1">Vendor Type</div>
          <RailBtn active={!fType} onClick={() => setFType('')} testid="vm-type-all">All types</RailBtn>
          {VENDOR_TYPES.map((t) => (
            <RailBtn key={t.id} active={fType === t.id} onClick={() => setFType(fType === t.id ? '' : t.id)} testid={`vm-type-${t.id}`}>{t.label}</RailBtn>
          ))}
          <div className="kpi-overline px-2 pt-3">Empanelment</div>
          {['', 'approved', 'pending', 'blacklisted'].map((s) => (
            <RailBtn key={s || 'all'} active={fEmp === s} onClick={() => setFEmp(s)} testid={`vm-emp-${s || 'all'}`}>
              {s ? s[0].toUpperCase() + s.slice(1) : 'All'}
            </RailBtn>
          ))}
          <div className="kpi-overline px-2 pt-3">Compliance</div>
          {[['', 'All'], ['expiring', 'Has expiring docs'], ['expired', 'Has expired docs']].map(([v, l]) => (
            <RailBtn key={v || 'all'} active={fCompliance === v} onClick={() => setFCompliance(v)} testid={`vm-comp-${v || 'all'}`}>{l}</RailBtn>
          ))}
          <div className="kpi-overline px-2 pt-3">Registration</div>
          {[['', 'All'], ['incomplete', 'Registration incomplete'], ['complete', 'Registration complete']].map(([v, l]) => (
            <RailBtn key={v || 'all'} active={fReg === v} onClick={() => setFReg(v)} testid={`vm-reg-${v || 'all'}`}>{l}</RailBtn>
          ))}
          <div className="kpi-overline px-2 pt-3">Approval</div>
          <RailBtn active={fSelf} onClick={() => setFSelf(x => !x)} testid="vm-self-approved-filter">Self-approved</RailBtn>
        </aside>

        {/* Main grid */}
        <div className="flex-1 min-w-0 space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, code, GSTIN, contact…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm bg-white focus:outline-none focus:border-[#3b4872]"
              data-testid="vm-search-input"
            />
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-400 text-xs uppercase tracking-widest font-display bg-white border border-slate-200 rounded-sm" data-testid="vm-empty">
              No vendors found — add your first vendor
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="vm-card-grid">
              {items.map((v) => (
                <VendorCard
                  key={v.id}
                  v={v}
                  onClick={() => setSelected(v)}
                  showSelect={fEmp === 'pending'}
                  selected={selectedIds.includes(v.id)}
                  onToggleSelect={() => toggleSelect(v.id)}
                />
              ))}
            </div>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-600 pt-1">
              <span>Page {page} of {pages} · {total} vendors</span>
              <div className="flex gap-1">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2.5 py-1 border border-slate-200 rounded-sm disabled:opacity-40 bg-white" data-testid="vm-prev-page">Prev</button>
                <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-2.5 py-1 border border-slate-200 rounded-sm disabled:opacity-40 bg-white" data-testid="vm-next-page">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && <VendorDrawer vendorId={selected.id} onClose={() => { setSelected(null); load(); }} />}
      {addOpen && <AddVendorSheet onClose={(saved) => { setAddOpen(false); if (saved) load(); }} />}
      {importOpen && (
        <VendorImportWizard
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); }}
        />
      )}
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, small, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm px-3 py-2.5" data-testid={testid}>
    <div className="kpi-overline flex items-center gap-1.5"><Icon size={11} /> {label}</div>
    <div className={`font-display font-bold ${small ? 'text-sm' : 'text-xl'} text-slate-900 mt-0.5`}>{value}</div>
  </div>
);

const RailBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-2 py-1.5 rounded-sm text-xs font-display ${active ? 'bg-[#3b4872] text-white font-bold' : 'text-slate-600 hover:bg-slate-100'}`}
    data-testid={testid}
  >
    {children}
  </button>
);

function VendorCard({ v, onClick, showSelect = false, selected = false, onToggleSelect }) {
  const docs = v.compliance_docs || [];
  const expiring = docs.filter((d) => d.status === 'expiring_soon').length;
  const expired = docs.filter((d) => d.status === 'expired').length;
  return (
    <div className="relative">
      {showSelect && (
        <label className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-white/95 px-1 py-0.5 rounded border border-slate-200" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="w-4 h-4 accent-[#3b4872]"
            data-testid={`vm-select-${v.id}`}
          />
        </label>
      )}
      <button onClick={onClick} className="text-left w-full bg-white border border-slate-200 rounded-sm p-3 hover:border-[#3b4872] hover:shadow-sm transition-colors" data-testid={`vm-card-${v.vendor_code}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] text-slate-500">{v.vendor_code}</span>
        <Stars score={v.rating?.overall_score} />
      </div>
      <div className="font-display font-bold text-sm text-slate-900 mt-0.5 truncate">{v.name}</div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {(v.vendor_type || []).map((t) => (
          <span key={t} className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded-sm font-display">{typeLabel(t)}</span>
        ))}
        {v.msme_registered && <span className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded-sm font-display">MSME</span>}
      </div>
      <div className="text-[11px] text-slate-500 mt-1.5 truncate">
        {v.gstin ? `GSTIN: ${v.gstin}` : 'No GSTIN'} · TDS: {v.tds_section || '—'}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <span className={`px-1.5 py-0.5 text-[10px] border rounded-sm font-display font-bold uppercase tracking-wide ${EMP_BADGE[v.empanelment_status] || EMP_BADGE.suspended}`}>
          {v.empanelment_status}
        </span>
        {v.registration_complete === false && (
          <span className="px-1.5 py-0.5 text-[10px] bg-orange-50 text-orange-700 border border-orange-200 rounded-sm font-display font-bold uppercase tracking-wide" data-testid={`vendor-incomplete-${v.id}`}>
            Incomplete Reg.
          </span>
        )}
        {expired > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-red-50 text-red-700 rounded-sm font-display">⚠ {expired} expired doc{expired > 1 ? 's' : ''}</span>}
        {expiring > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-amber-50 text-amber-700 rounded-sm font-display">⚠ {expiring} expiring</span>}
        <span className="ml-auto text-[10px] text-slate-400">{v.rating?.total_orders || 0} orders</span>
      </div>
    </button>
    </div>
  );
}

/* ── Vendor profile drawer ─────────────────────────────────────────────── */
function VendorDrawer({ vendorId, onClose }) {
  const { user } = useAuth() || {};
  const canEmpanel = !!user?.is_procurement_approver;
  const [v, setV] = useState(null);
  const [tab, setTab] = useUrlTab('tab', 'overview');
  const [orders, setOrders] = useState(null);
  const [docForm, setDocForm] = useState(null);
  const [rcForm, setRcForm] = useState(null);
  const [showCompleteRegForm, setShowCompleteRegForm] = useState(false);

  const reload = useCallback(() => {
    apiGet(`/vendors/${vendorId}`).then(setV).catch(() => toast.error('Failed to load vendor'));
  }, [vendorId]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (tab === 'orders' && orders === null) {
      apiGet(`/vendors/${vendorId}/orders`).then((d) => setOrders(d?.items || [])).catch(() => setOrders([]));
    }
  }, [tab, orders, vendorId]);

  const empanel = async () => {
    try { await apiPost(`/vendors/${vendorId}/empanel`); toast.success('Vendor empanelled'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const requestEmpanel = async () => {
    try { await apiPost(`/vendors/${vendorId}/empanel-request`); toast.success('Empanelment requested — the Procurement team has been notified'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const blacklist = async () => {
    const reason = window.prompt('Blacklist reason:');
    if (!reason) return;
    try { await apiPost(`/vendors/${vendorId}/blacklist`, { reason }); toast.success('Vendor blacklisted'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const expiredDocs = (v?.compliance_docs || []).filter((d) => d.status === 'expired');

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="vm-drawer">
        {!v ? (
          <>
            <SheetHeader>
              <SheetTitle className="font-display">Vendor Profile</SheetTitle>
              <SheetDescription>Loading…</SheetDescription>
            </SheetHeader>
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>
          </>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display flex items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{v.vendor_code}</span> {v.name}
              </SheetTitle>
              <SheetDescription className="flex items-center gap-2 flex-wrap">
                <span className={`px-1.5 py-0.5 text-[10px] border rounded-sm font-display font-bold uppercase ${EMP_BADGE[v.empanelment_status]}`}>{v.empanelment_status}</span>
                {v.registration_complete === false && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-orange-50 text-orange-700 border border-orange-200 rounded-sm font-display font-bold uppercase" title={`Missing: ${(v.registration_missing || []).join(', ')}`}>
                    Incomplete Registration — manual POs blocked
                  </span>
                )}
                {(v.vendor_type || []).map((t) => <span key={t} className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded-sm">{typeLabel(t)}</span>)}
                <Stars score={v.rating?.overall_score} />
              </SheetDescription>
            </SheetHeader>

            {canEmpanel && v.empanelment_status !== 'approved' && !v.registration_complete && (() => {
              const have = new Set((v.compliance_docs || []).map(d => d.doc_type || d.type));
              const miss = EMPANEL_DOCS.filter(d => !have.has(d));
              return miss.length > 0 ? (
                <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-800" data-testid="vm-empanel-missing-docs">
                  Before empanelment, upload: {miss.join(', ')}{(v.registration_missing || []).length ? ` · complete: ${v.registration_missing.join(', ')}` : ''}
                </div>
              ) : null;
            })()}
            {expiredDocs.length > 0 && (
              <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700 flex items-center gap-2" data-testid="vm-expired-banner">
                <ShieldAlert size={14} /> {expiredDocs.length} compliance document{expiredDocs.length > 1 ? 's' : ''} expired — bill processing will be blocked.
              </div>
            )}

            <div className="flex items-center gap-2 mt-3">
              {v.empanelment_status !== 'approved' && canEmpanel && (() => {
                const have = new Set((v.compliance_docs || []).map(d => d.doc_type || d.type));
                const miss = v.registration_complete ? [] : EMPANEL_DOCS.filter(d => !have.has(d));
                return (
                  <span title={miss.length ? `Upload first: ${miss.join(', ')}` : ''} data-testid="vm-empanel-btn-wrap">
                    <button onClick={empanel} disabled={miss.length > 0} className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-display font-bold disabled:bg-slate-300 disabled:cursor-not-allowed" data-testid="vm-empanel-btn">
                      <CheckCircle2 size={12} /> Empanel
                    </button>
                  </span>
                );
              })()}
              {v.empanelment_status === 'pending' && !canEmpanel && (
                <>
                  <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm" data-testid="vm-waiting-empanel">Waiting for empanelment by the Procurement team</span>
                  {!v.empanelment_requested_at && (
                    <button onClick={requestEmpanel} className="px-2.5 py-1.5 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-display font-bold" data-testid="vm-request-empanel-btn">Request empanelment</button>
                  )}
                </>
              )}
              {v.self_approved && v.self_approved_basis === 'procurement_group' && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-sm font-bold" data-testid="vm-self-approved-badge">Self-approved (Procurement team)</span>
              )}
              {v.empanelment_status !== 'blacklisted' && (canEmpanel || v.empanelment_status !== 'pending') && (
                <button onClick={blacklist} className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-sm font-display font-bold" data-testid="vm-blacklist-btn">
                  <Ban size={12} /> Blacklist
                </button>
              )}
              {v.registration_complete === false && (
                <button onClick={() => setShowCompleteRegForm(true)}
                  data-testid="vm-complete-registration-btn"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm font-display font-bold">
                  <CheckCircle2 size={12} /> Complete Registration
                </button>
              )}
            </div>
            {showCompleteRegForm && (
              <CompleteRegistrationForm
                vendor={v}
                missingFields={v.registration_missing || []}
                onClose={() => setShowCompleteRegForm(false)}
                onSaved={(updated) => {
                  setV(updated);
                  setShowCompleteRegForm(false);
                  toast.success('Registration completed — vendor is now fully active');
                }}
              />
            )}

            {/* Tabs */}
            <div className="flex gap-1 mt-4 border-b border-slate-200">
              {[['overview', 'Overview'], ['compliance', 'Compliance Docs'], ['rates', 'Rate Contracts'], ['orders', 'Order History'], ['rating', 'Rating']].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)}
                  className={`px-3 py-2 text-xs font-display font-bold uppercase tracking-wide border-b-2 -mb-px ${tab === id ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                  data-testid={`vm-tab-${id}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-4">
              {tab === 'overview' && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-testid="vm-overview">
                  <Field icon={Phone} label="Contact" value={`${v.contact_person || '—'} · ${v.phone || '—'}`} />
                  <Field icon={Mail} label="Email" value={v.email || '—'} />
                  <Field icon={MapPin} label="Address" value={[v.address, v.city, v.state, v.pincode].filter(Boolean).join(', ') || '—'} />
                  <Field icon={FileText} label="GSTIN / PAN" value={`${v.gstin || '—'} / ${v.pan || '—'}`} />
                  <Field icon={FileText} label="TDS Section" value={v.tds_section || '—'} />
                  <Field icon={FileText} label="MSME" value={v.msme_registered ? (v.msme_number || 'Registered') : 'No'} />
                  <Field icon={Landmark} label="Bank" value={v.bank_name ? `${v.bank_name} · ${v.bank_account} · ${v.bank_ifsc}` : '—'} />
                  <Field icon={Clock} label="Empanelled" value={v.empanelled_on ? formatDate(v.empanelled_on) : '—'} />
                  {v.blacklist_reason && <Field icon={Ban} label="Blacklist Reason" value={v.blacklist_reason} />}
                  {v.notes && <div className="col-span-2 text-xs text-slate-600 bg-slate-50 p-2 rounded-sm">{v.notes}</div>}
                </div>
              )}

              {tab === 'compliance' && (
                <div data-testid="vm-compliance">
                  <table className="w-full dense-table">
                    <thead><tr><th>Document</th><th>No.</th><th>Expiry</th><th>Status</th></tr></thead>
                    <tbody>
                      {(v.compliance_docs || []).length === 0 && <tr><td colSpan="4" className="text-center py-6 text-slate-400 text-xs">No compliance documents</td></tr>}
                      {(v.compliance_docs || []).map((d) => (
                        <tr key={d.id}>
                          <td className="text-xs">{d.doc_type.replace(/_/g, ' ')}</td>
                          <td className="font-mono text-xs">{d.doc_no || '—'}</td>
                          <td className="text-xs">{d.expiry_date ? formatDate(d.expiry_date) : 'Perpetual'}</td>
                          <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${DOC_BADGE[d.status] || ''}`}>{(d.status || '').replace('_', ' ')}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {docForm ? (
                    <ComplianceDocForm vendorId={vendorId} onDone={() => { setDocForm(null); reload(); }} onCancel={() => setDocForm(null)} />
                  ) : (
                    <button onClick={() => setDocForm({})} className="mt-3 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="vm-add-doc-btn">
                      <Plus size={11} className="inline mr-1" />Add Document
                    </button>
                  )}
                </div>
              )}

              {tab === 'rates' && (
                <div data-testid="vm-rates" className="space-y-3">
                  {(v.rate_contracts || []).length === 0 && <div className="text-center py-6 text-slate-400 text-xs">No rate contracts</div>}
                  {(v.rate_contracts || []).map((rc) => (
                    <div key={rc.id} className="border border-slate-200 rounded-sm p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-sm">{rc.description}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${rc.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{rc.status}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(rc.valid_from)} → {formatDate(rc.valid_to)} · {(rc.categories || []).join(', ')}</div>
                      {(rc.line_items || []).length > 0 && (
                        <table className="w-full dense-table mt-2">
                          <thead><tr><th>Item</th><th>UOM</th><th className="text-right">Rate</th><th>HSN</th></tr></thead>
                          <tbody>
                            {rc.line_items.map((li, i) => (
                              <tr key={i}><td className="text-xs">{li.item}</td><td className="text-xs">{li.uom}</td><td className="text-right tabular-nums text-xs">{formatINR(li.rate)}</td><td className="font-mono text-xs">{li.hsn}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  ))}
                  {rcForm ? (
                    <RateContractForm vendorId={vendorId} onDone={() => { setRcForm(null); reload(); }} onCancel={() => setRcForm(null)} />
                  ) : (
                    <button onClick={() => setRcForm({})} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold" data-testid="vm-add-rc-btn">
                      <Plus size={11} className="inline mr-1" />Add Rate Contract
                    </button>
                  )}
                </div>
              )}

              {tab === 'orders' && (
                <div data-testid="vm-orders">
                  {orders === null ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" /></div> : (
                    <table className="w-full dense-table">
                      <thead><tr><th>Type</th><th>Doc No</th><th>Title / Item</th><th>Project</th><th className="text-right">Value</th><th>Status</th></tr></thead>
                      <tbody>
                        {orders.length === 0 && <tr><td colSpan="6" className="text-center py-6 text-slate-400 text-xs">No orders yet</td></tr>}
                        {orders.map((o, i) => (
                          <tr key={i}>
                            <td className="text-[10px] uppercase font-display">{o.doc_type === 'work_order' ? 'WO' : o.doc_type.toUpperCase()}</td>
                            <td className="font-mono text-xs">{o.doc_no}</td>
                            <td className="text-xs max-w-[180px] truncate">{o.title}</td>
                            <td className="text-xs">{o.project}</td>
                            <td className="text-right tabular-nums text-xs">{formatINR(o.value)}</td>
                            <td className="text-xs">{o.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {tab === 'rating' && (
                <div className="grid grid-cols-2 gap-3" data-testid="vm-rating">
                  <RatingTile label="Overall Score" value={`${(v.rating?.overall_score || 0).toFixed(1)} / 5`} />
                  <RatingTile label="On-time %" value={`${(v.rating?.on_time_pct || 0).toFixed(0)}%`} />
                  <RatingTile label="Quality Rejection %" value={`${(v.rating?.quality_rejection_pct || 0).toFixed(1)}%`} />
                  <RatingTile label="Shortage %" value={`${(v.rating?.shortage_pct || 0).toFixed(1)}%`} />
                  <RatingTile label="Total Orders" value={v.rating?.total_orders || 0} />
                  <RatingTile label="Last Updated" value={v.rating?.last_updated ? formatDate(v.rating.last_updated) : '—'} />
                  <div className="col-span-2 text-[11px] text-slate-500">Rating is auto-updated as GRNs, WPCs and hire logs close against this vendor.</div>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Field = ({ icon: Icon, label, value }) => (
  <div>
    <div className="kpi-overline flex items-center gap-1"><Icon size={10} /> {label}</div>
    <div className="text-xs text-slate-800 mt-0.5">{value}</div>
  </div>
);

const FIELD_LABELS = {
  gstin: 'GSTIN', pan: 'PAN', address: 'Address',
  contact_person: 'Contact Person', phone: 'Phone',
  bank_name: 'Bank Name', bank_account: 'Bank Account Number', bank_ifsc: 'Bank IFSC Code',
};

function CompleteRegistrationForm({ vendor, missingFields, onClose, onSaved }) {
  const [form, setForm] = useState(
    Object.fromEntries((missingFields || []).map(f => [f, vendor[f] || '']))
  );
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const stillEmpty = (missingFields || []).filter(f => !String(form[f] || '').trim());
    if (stillEmpty.length) return toast.error(`Please fill: ${stillEmpty.map(f => FIELD_LABELS[f] || f).join(', ')}`);
    setSaving(true);
    try {
      const res = await apiPut(`/vendors/${vendor.id}`, { ...vendor, ...form });
      if ((res?.data || res)?.state_warning) toast.warning((res?.data || res).state_warning);
      onSaved(res?.data || res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-display font-bold">Complete Vendor Registration</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">Required before manual POs can be issued to this vendor.</p>
          {(missingFields || []).map(f => (
            <div key={f}>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{FIELD_LABELS[f] || f}</label>
              <input
                value={form[f] || ''}
                onChange={(e) => setForm(p => ({ ...p, [f]: e.target.value }))}
                className="w-full mt-0.5 px-3 py-2 border border-slate-300 rounded-sm text-sm"
                data-testid={`vm-reg-field-${f}`}
              />
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-4 py-2 border border-slate-300 rounded-sm">Cancel</button>
          <button onClick={submit} disabled={saving}
            className="text-xs px-4 py-2 bg-[#3b4872] text-white rounded-sm font-bold disabled:opacity-50"
            data-testid="vm-reg-save-btn">
            {saving ? 'Saving…' : 'Save & Activate Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
}

const RatingTile = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
    <div className="kpi-overline">{label}</div>
    <div className="font-display font-bold text-lg text-slate-900 mt-0.5">{value}</div>
  </div>
);

function ComplianceDocForm({ vendorId, onDone, onCancel }) {
  const [f, setF] = useState({ doc_type: 'gst_certificate', doc_no: '', issued_date: '', expiry_date: '', file_url: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await apiPost(`/vendors/${vendorId}/compliance-doc`, { ...f, issued_date: f.issued_date || null, expiry_date: f.expiry_date || null });
      toast.success('Document added'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50" data-testid="vm-doc-form">
      <div className="grid grid-cols-2 gap-2">
        <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white" data-testid="vm-doc-type">
          {DOC_TYPES.map((d) => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
        </select>
        <input placeholder="Doc number" value={f.doc_no} onChange={(e) => setF({ ...f, doc_no: e.target.value })} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="vm-doc-no" />
        <label className="text-[10px] text-slate-500">Issued<input type="date" value={f.issued_date} onChange={(e) => setF({ ...f, issued_date: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 mt-0.5" /></label>
        <label className="text-[10px] text-slate-500">Expiry (blank = perpetual)<input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 mt-0.5" data-testid="vm-doc-expiry" /></label>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="vm-doc-save">{saving ? 'Saving…' : 'Save'}</button>
        <button onClick={onCancel} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
      </div>
    </div>
  );
}

function RateContractForm({ vendorId, onDone, onCancel }) {
  const [f, setF] = useState({ description: '', valid_from: '', valid_to: '', categories: '' });
  const [lines, setLines] = useState([{ item: '', uom: '', rate: '', hsn: '' }]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.description || !f.valid_from || !f.valid_to) { toast.error('Description and validity dates are required'); return; }
    setSaving(true);
    try {
      await apiPost(`/vendors/${vendorId}/rate-contract`, {
        ...f,
        categories: f.categories.split(',').map((s) => s.trim()).filter(Boolean),
        line_items: lines.filter((l) => l.item).map((l) => ({ ...l, rate: Number(l.rate) || 0 })),
      });
      toast.success('Rate contract added'); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50" data-testid="vm-rc-form">
      <input placeholder="Description (e.g. Annual steel rate contract FY 25-26)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5" data-testid="vm-rc-desc" />
      <div className="grid grid-cols-3 gap-2">
        <label className="text-[10px] text-slate-500">Valid from<input type="date" value={f.valid_from} onChange={(e) => setF({ ...f, valid_from: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 mt-0.5" data-testid="vm-rc-from" /></label>
        <label className="text-[10px] text-slate-500">Valid to<input type="date" value={f.valid_to} onChange={(e) => setF({ ...f, valid_to: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 mt-0.5" data-testid="vm-rc-to" /></label>
        <label className="text-[10px] text-slate-500">Categories (comma sep)<input value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })} className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 mt-0.5" /></label>
      </div>
      <div className="kpi-overline">Line Items</div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-4 gap-2">
          <input placeholder="Item" value={l.item} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, item: e.target.value } : x))} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" />
          <input placeholder="UOM" value={l.uom} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, uom: e.target.value } : x))} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" />
          <input placeholder="Rate" type="number" value={l.rate} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" />
          <input placeholder="HSN" value={l.hsn} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, hsn: e.target.value } : x))} className="text-xs border border-slate-200 rounded-sm px-2 py-1.5" />
        </div>
      ))}
      <button onClick={() => setLines([...lines, { item: '', uom: '', rate: '', hsn: '' }])} className="text-xs text-[#3b4872] font-display font-bold">+ Add line</button>
      <div className="flex gap-2 pt-1">
        <button onClick={save} disabled={saving} className="px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="vm-rc-save">{saving ? 'Saving…' : 'Save Contract'}</button>
        <button onClick={onCancel} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
      </div>
    </div>
  );
}

/* ── Add vendor sheet ──────────────────────────────────────────────────── */
function AddVendorSheet({ onClose }) {
  const [f, setF] = useState({
    name: '', short_name: '', vendor_type: [], categories: '',
    address: '', city: '', state: '', pincode: '',
    contact_person: '', phone: '', email: '', website: '',
    gstin: '', pan: '', msme_registered: false, msme_number: '', tds_section: '',
    bank_name: '', bank_account: '', bank_ifsc: '', notes: '',
    empanel_immediately: false,
  });
  const [saving, setSaving] = useState(false);

  const toggleType = (t) => setF({ ...f, vendor_type: f.vendor_type.includes(t) ? f.vendor_type.filter((x) => x !== t) : [...f.vendor_type, t] });

  const save = async () => {
    if (!f.name.trim()) { toast.error('Vendor name is required'); return; }
    if (f.vendor_type.length === 0) { toast.error('Pick at least one vendor type'); return; }
    setSaving(true);
    try {
      const created = await apiPost('/vendors', {
        ...f,
        categories: f.categories.split(',').map((s) => s.trim()).filter(Boolean),
        tds_section: f.tds_section || null,
      });
      if (created?.state_warning) toast.warning(created.state_warning);
      toast.success('Vendor created');
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create vendor'); }
    finally { setSaving(false); }
  };

  const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#3b4872]';
  return (
    <Sheet open onOpenChange={(o) => !o && onClose(false)}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="vm-add-sheet">
        <SheetHeader>
          <SheetTitle className="font-display">Add Vendor</SheetTitle>
          <SheetDescription>Basic info → Statutory → Bank. Compliance docs can be added from the profile after creation.</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <section>
            <div className="kpi-overline mb-2">Basic Info</div>
            <div className="space-y-2">
              <input placeholder="Legal / trading name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="vm-form-name" />
              <input placeholder="Short name / alias" value={f.short_name} onChange={(e) => setF({ ...f, short_name: e.target.value })} className={inp} />
              <div className="flex flex-wrap gap-1.5">
                {VENDOR_TYPES.map((t) => (
                  <button key={t.id} onClick={() => toggleType(t.id)}
                    className={`px-2 py-1 text-[11px] rounded-sm border font-display ${f.vendor_type.includes(t.id) ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    data-testid={`vm-form-type-${t.id}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <input placeholder="Categories they supply / work (comma separated: steel, civil, MEP…)" value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })} className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Contact person" value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} className={inp} />
                <input placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} />
                <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} />
                <input placeholder="Website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={inp} />
              </div>
              <input placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className={inp} />
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={inp} />
                <input placeholder="State" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className={inp} />
                <input placeholder="Pincode" value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} className={inp} />
              </div>
            </div>
          </section>

          <section>
            <div className="kpi-overline mb-2">Statutory</div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="GSTIN (15 chars)" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} className={inp} data-testid="vm-form-gstin" />
              <input placeholder="PAN (10 chars)" value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className={inp} data-testid="vm-form-pan" />
              <select value={f.tds_section} onChange={(e) => setF({ ...f, tds_section: e.target.value })} className={inp} data-testid="vm-form-tds">
                <option value="">TDS — auto-suggest from type</option>
                {['194C', '194J', '194I', '194Q', 'none'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input placeholder="MSME number (if registered)" value={f.msme_number} onChange={(e) => setF({ ...f, msme_number: e.target.value, msme_registered: !!e.target.value })} className={inp} />
            </div>
          </section>

          <section>
            <div className="kpi-overline mb-2">Bank</div>
            <div className="grid grid-cols-3 gap-2">
              <input placeholder="Bank name" value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} className={inp} />
              <input placeholder="Account no." value={f.bank_account} onChange={(e) => setF({ ...f, bank_account: e.target.value })} className={inp} />
              <input placeholder="IFSC" value={f.bank_ifsc} onChange={(e) => setF({ ...f, bank_ifsc: e.target.value.toUpperCase() })} className={inp} />
            </div>
          </section>

          <textarea placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inp} h-16`} />

          {/* Batch D §9: "Empanel immediately (skip pending approval)" tick removed.
              Vendors are created in Pending status; a different authorised person must empanel. */}

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button onClick={save} disabled={saving} className="px-4 py-2 text-xs bg-[#3b4872] hover:bg-[#2d3856] text-white rounded-sm font-display font-bold uppercase tracking-wider disabled:opacity-50" data-testid="vm-form-save">
              {saving ? 'Creating…' : 'Create Vendor'}
            </button>
            <button onClick={() => onClose(false)} className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-sm font-display">Cancel</button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
